import { initReviewCyclesI18next } from "@domains/reviews/client/i18n/i18next.ts";
import { initEngagementI18next } from "@domains/engagement/client/i18n/i18next.ts";
import { ENGAGEMENT_PERIOD_QUERY_KEY, ENGAGEMENT_SCOPE_QUERY_KEY } from "@domains/engagement/shared/queryKeys.ts";
import { AdjustmentsHorizontalIcon, ChartBarIcon, ChatBubbleLeftIcon, ChatBubbleLeftRightIcon, RectangleGroupIcon, SignalIcon, StarIcon, UsersIcon } from "@heroicons/react/24/outline";
import { hasAnyRole } from "@packages/core/session/roles.ts";
import { initScopeI18next } from "@packages/core-client/scope/i18n/i18next.ts";
import type { AppDeferredRegistrationData, AppModuleRegisterFunction } from "@packages/core-client/registration/appModuleRegisterFunction.ts";
import { registerArea } from "@packages/core-client/registration/registerArea.tsx";
import { useFeatureFlags, type FireflyRuntime } from "@squide/firefly";
import type { ReactNode } from "react";
import { Navigate } from "react-router";
import { ANALYTICS_ACCESS } from "#shared/access.ts";
import { AnalyticsFiltersCard } from "./components/AnalyticsFiltersCard.tsx";
import { EngagementFiltersHeading } from "./components/EngagementFiltersHeading.tsx";
import { FeedbackFiltersHeading } from "./components/FeedbackFiltersHeading.tsx";
import { ANALYTICS_FILTER_QUERY_KEYS } from "./hooks/useAnalyticsFilters.ts";
import { COMPARISON_QUERY_KEYS } from "./hooks/useComparisonSelections.ts";
import { FEEDBACK_FILTER_QUERY_KEYS, FEEDBACK_PERIOD_QUERY_KEY, FEEDBACK_SCOPE_QUERY_KEY } from "./hooks/useFeedbackFilters.ts";
import { initI18next } from "./i18n/i18next.ts";

async function registerMsw(runtime: FireflyRuntime) {
    if (runtime.isMswEnabled) {
        const requestHandlers = (await import("./api/mocks/getRequestHandlers.ts")).getRequestHandlers();

        runtime.registerRequestHandlers(requestHandlers);
    }
}

interface EngagementLandingGuardProps {
    children: ReactNode;
}

function EngagementLandingGuard({ children }: EngagementLandingGuardProps) {
    const flags = useFeatureFlags();

    if (!flags["engagement-analytics"]) {
        return <Navigate to="/analytics/performance" replace />;
    }

    return children;
}

export const registerAnalytics: AppModuleRegisterFunction = async runtime => {
    const i18nextInstance = initI18next(runtime);
    initReviewCyclesI18next(runtime);
    initScopeI18next(runtime);
    initEngagementI18next(runtime);
    initReviewCyclesI18next(runtime);

    await registerMsw(runtime);

    const area = registerArea(runtime, "analytics", "/analytics", ChartBarIcon, i18nextInstance, { access: ANALYTICS_ACCESS });

    const engagementSection = area.registerSidebarArea("engagement", { hideLabel: true });
    const engagement = engagementSection.registerTabsArea("main", "/engagement", { landing: true, preserveSearchParams: [ENGAGEMENT_SCOPE_QUERY_KEY, ENGAGEMENT_PERIOD_QUERY_KEY], Heading: EngagementFiltersHeading });

    const engagementOverview = engagement.registerRoute("overview", {
        index: true,
        page: "AnalyticsEngagement",
        lazy: async () => {
            const { EngagementOverviewPage } = await import("./pages/engagement/overview/EngagementOverviewPage.tsx");

            return {
                element: (
                    <EngagementLandingGuard>
                        <EngagementOverviewPage />
                    </EngagementLandingGuard>
                )
            };
        }
    });

    const engagementMetrics = engagement.registerRoute("metrics", {
        path: "/metrics",
        page: "AnalyticsEngagement",
        lazy: async () => {
            const { EngagementMetricsPage } = await import("./pages/engagement/metrics/EngagementMetricsPage.tsx");

            return { element: <EngagementMetricsPage /> };
        }
    });

    const engagementQuestionResults = engagement.registerRoute("question-results", {
        path: "/question-results",
        page: "AnalyticsEngagement",
        lazy: async () => {
            const { EngagementQuestionResultsPage } = await import("./pages/engagement/question-results/EngagementQuestionResultsPage.tsx");

            return { element: <EngagementQuestionResultsPage /> };
        }
    });

    const engagementHeatmap = engagement.registerRoute("heatmap", {
        path: "/heatmap",
        page: "AnalyticsEngagement",
        lazy: async () => {
            const { EngagementHeatmapPage } = await import("./pages/engagement/heatmap/EngagementHeatmapPage.tsx");

            return { element: <EngagementHeatmapPage /> };
        }
    });

    engagementSection.registerRoute("metric-detail", {
        path: "/engagement/metrics/:metricId",
        page: "AnalyticsEngagementMetricDetail",
        noHeading: true,
        lazy: async () => {
            const { EngagementMetricDetailPage } = await import("./pages/engagement/metrics/EngagementMetricDetailPage.tsx");
            const { EngagementMetricDetailErrorBoundary } = await import("./pages/engagement/metrics/EngagementMetricDetailErrorBoundary.tsx");

            return { element: <EngagementMetricDetailPage />, errorElement: <EngagementMetricDetailErrorBoundary /> };
        }
    });

    engagementSection.registerRoute("custom-question-detail", {
        path: "/engagement/question-results/:questionId",
        page: "AnalyticsEngagementCustomQuestionDetail",
        noHeading: true,
        lazy: async () => {
            const { CustomQuestionDetailPage } = await import("./pages/engagement/question-results/detail/CustomQuestionDetailPage.tsx");
            const { CustomQuestionDetailErrorBoundary } = await import("./pages/engagement/question-results/detail/CustomQuestionDetailErrorBoundary.tsx");

            return { element: <CustomQuestionDetailPage />, errorElement: <CustomQuestionDetailErrorBoundary /> };
        }
    });

    const feedbackSection = area.registerSidebarArea("feedback", { hideLabel: true });
    const feedback = feedbackSection.registerTabsArea("main", "/feedback", { preserveSearchParams: [FEEDBACK_SCOPE_QUERY_KEY, FEEDBACK_PERIOD_QUERY_KEY, ...FEEDBACK_FILTER_QUERY_KEYS], Heading: FeedbackFiltersHeading });

    const feedbackOverview = feedback.registerRoute("overview", {
        index: true,
        page: "AnalyticsFeedback",
        lazy: async () => {
            const { FeedbackOverviewPage } = await import("./pages/feedback/overview/FeedbackOverviewPage.tsx");

            return { element: <FeedbackOverviewPage /> };
        }
    });

    const performanceSection = area.registerSidebarArea("performance", { hideLabel: true });
    const performance = performanceSection.registerTabsArea("main", "/performance", { preserveSearchParams: [...ANALYTICS_FILTER_QUERY_KEYS, ...COMPARISON_QUERY_KEYS], Heading: AnalyticsFiltersCard });

    const performanceOverview = performance.registerRoute("overview", {
        index: true,
        page: "PerformanceAnalytics",
        lazy: async () => {
            const { PerformanceOverviewPage } = await import("./pages/performance/overview/PerformanceOverviewPage.tsx");

            return { element: <PerformanceOverviewPage /> };
        }
    });

    const performanceCalibration = performance.registerRoute("calibration", {
        path: "/calibration",
        page: "PerformanceAnalytics",
        lazy: async () => {
            const { PerformanceCalibrationPage } = await import("./pages/performance/calibration/PerformanceCalibrationPage.tsx");

            return { element: <PerformanceCalibrationPage /> };
        }
    });

    const performanceThemes = performance.registerRoute("themes", {
        path: "/themes",
        page: "PerformanceAnalytics",
        lazy: async () => {
            const { PerformanceThemesPage } = await import("./pages/performance/themes/PerformanceThemesPage.tsx");

            return { element: <PerformanceThemesPage /> };
        }
    });

    const managerImpactSection = area.registerSidebarArea("manager-impact", { hideLabel: true });
    const managerImpact = managerImpactSection.registerTabsArea("main", "/manager-impact", { hideTitle: true, useRenderAsTitleOnly: () => true });

    const managerImpactOverview = managerImpact.registerRoute("overview", {
        index: true,
        lazy: async () => {
            const { ManagerImpactHrPage } = await import("./pages/manager-impact/ManagerImpactHrPage.tsx");

            return { element: <ManagerImpactHrPage /> };
        }
    });

    return (deferredRuntime: FireflyRuntime, { session }: AppDeferredRegistrationData) => {
        if (!hasAnyRole(session, ANALYTICS_ACCESS)) {
            return;
        }

        const isEngagementEnabled = deferredRuntime.getFeatureFlag("engagement-analytics", false);

        if (isEngagementEnabled) {
            engagement.registerLink({ priority: 690, icon: SignalIcon });
            engagementOverview.registerLink({ priority: 1000 });
            engagementMetrics.registerLink({ priority: 999 });
            engagementQuestionResults.registerLink({ priority: 998 });
            engagementHeatmap.registerLink({ priority: 997 });
        }

        if (deferredRuntime.getFeatureFlag("feedback-analytics", false)) {
            feedback.registerLink({ priority: 640, icon: ChatBubbleLeftIcon });
            feedbackOverview.registerLink({ priority: 1000 });
        }

        performance.registerLink({ priority: 590, icon: StarIcon });
        performanceOverview.registerLink({ priority: 1000, icon: RectangleGroupIcon });
        performanceCalibration.registerLink({ priority: 999, icon: AdjustmentsHorizontalIcon });
        performanceThemes.registerLink({ priority: 998, icon: ChatBubbleLeftRightIcon });

        const isManagerImpactHrEnabled = deferredRuntime.getFeatureFlag("manager-impact-hr-report", false);

        if (isManagerImpactHrEnabled) {
            managerImpact.registerLink({ priority: 490, icon: UsersIcon });
            managerImpactOverview.registerLink({ priority: 1000 });
        }

        area.registerToTopNav({ priority: 100 });
    };
};
